import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alertDialog";

const COUNTDOWN_SECONDS = 60;

function formatTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * competing: can reclaim with "Continue here" or sign out.
 * kicked: this tab used "Force login" and another device took the session — sign out only (no Continue).
 */
export default function ConcurrentSessionModal({
  open,
  variant = "competing",
  onContinueHere,
  onLogoutHere,
  continueLoading = false,
}) {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const intervalRef = useRef(null);
  const autoLogoutFiredRef = useRef(false);
  const onLogoutHereRef = useRef(onLogoutHere);
  onLogoutHereRef.current = onLogoutHere;

  const kicked = variant === "kicked";

  useEffect(() => {
    if (!open) {
      autoLogoutFiredRef.current = false;
      setSecondsLeft(COUNTDOWN_SECONDS);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    autoLogoutFiredRef.current = false;
    setSecondsLeft(COUNTDOWN_SECONDS);

    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          if (!autoLogoutFiredRef.current) {
            autoLogoutFiredRef.current = true;
            onLogoutHereRef.current();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open]);

  const handleManualLogout = () => {
    if (autoLogoutFiredRef.current) return;
    autoLogoutFiredRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    onLogoutHere();
  };

  return (
    <AlertDialog open={open} onOpenChange={() => {}}>
      <AlertDialogContent
        className="max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-semibold">
            {kicked ? "Session no longer active" : "Signed in on another device"}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3 text-left">
            {kicked ? (
              <p>
                This account is now active on another device. You can no longer continue on this
                browser from this sign-in.
              </p>
            ) : (
              <p>
                Your account was opened somewhere else. Choose whether to keep working on{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">this device</span> or
                sign out here.
              </p>
            )}
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              If you do nothing, you will be signed out on this device in{" "}
              <span className="font-mono font-semibold tabular-nums">{formatTime(secondsLeft)}</span>.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          {!kicked && (
            <Button
              type="button"
              className="w-full bg-[#078037] hover:bg-[#06602c]"
              disabled={continueLoading}
              onClick={() => onContinueHere()}
            >
              {continueLoading ? "Updating session…" : "Continue here"}
            </Button>
          )}
          <Button
            type="button"
            variant={kicked ? "default" : "outline"}
            className={
              kicked
                ? "w-full bg-[#078037] hover:bg-[#06602c]"
                : "w-full border-red-200 text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/50"
            }
            disabled={!kicked && continueLoading}
            onClick={handleManualLogout}
          >
            {kicked ? "Sign out" : "Log out here"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
